import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Button } from "../ui/Button";
import { formatNumber } from "../FinancialReview/format";
import { CreatableSelect } from "../FinancialReview/ui";
import { logRevopsMapping, logRevopsPricing } from "./api";

const INPUT_CLASS = "flex h-9 w-full min-w-[160px] rounded-md border border-input bg-background px-2 text-sm";
const isPlaceholderOption = (value) => {
  const v = String(value || "").trim().toLowerCase();
  return !v || v === "unknown" || v === "n/a";
};
const realOptions = (list, extras = []) =>
  [...new Set([...(list || []), ...extras].map(String).filter((item) => !isPlaceholderOption(item)))];

export default function CleaningNeededTab({ data, loading, error, mappingOptions, onLogged }) {
  const [drafts, setDrafts] = useState({});
  const [priceDrafts, setPriceDrafts] = useState({});
  const [created, setCreated] = useState({ specialties: [], super_orgs: [] });
  const [savingId, setSavingId] = useState(null);
  const [formError, setFormError] = useState(null);

  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const rows = data.cleaning_needed || [];
  const pricingRows = data.pricing_cleaning_needed || [];
  const specialties = realOptions(mappingOptions.specialties, created.specialties);
  const superOrgs = realOptions(mappingOptions.super_orgs, created.super_orgs);

  const updateDraft = (userId, field, value) => {
    setDrafts((prev) => ({ ...prev, [userId]: { ...prev[userId], [field]: value } }));
  };

  const remember = (field, value) => {
    setCreated((prev) => (prev[field].includes(value) ? prev : { ...prev, [field]: [...prev[field], value] }));
  };

  const handleLog = async (row) => {
    if (!row.has_doctor) {
      setFormError("Cannot log a mapping without a doctors row.");
      return;
    }
    const draft = drafts[row.user_id] || {};
    const specialty = (draft.specialty ?? row.specialty ?? "").trim();
    const org = (draft.org ?? row.super_org ?? "").trim();
    if (!specialty || !org || specialty.toLowerCase() === "unknown" || specialty.toLowerCase() === "n/a" || org.toLowerCase() === "n/a" || org.toLowerCase() === "unknown") {
      setFormError("A real specialty and super org are required to log a mapping.");
      return;
    }
    setSavingId(row.user_id);
    setFormError(null);
    try {
      await logRevopsMapping({ doctor_id: row.user_id, specialty, org });
      onLogged();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleLogPrice = async (row) => {
    const cost = Number(priceDrafts[row.super_org]);
    if (!Number.isFinite(cost) || cost <= 0) {
      setFormError("A cost per user greater than 0 is required.");
      return;
    }
    setSavingId(row.super_org);
    setFormError(null);
    try {
      await logRevopsPricing({ super_org: row.super_org, cost_per_user: cost });
      onLogged();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
    {formError && <p className="text-sm text-red-600">{formError}</p>}
    <Card>
      <CardHeader>
        <CardTitle>Mapping cleaning</CardTitle>
        <CardDescription>
          Activity users missing a doctors row, a revops specialty / super org, or with specialty/org Unknown or N/A in
          revops_orgs. Log writes revops_orgs only when a doctors row exists — doctors is never updated.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Monthly recordings</TableHead>
                <TableHead>Specialty</TableHead>
                <TableHead>Super org</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground">
                    No cleaning needed
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const draft = drafts[row.user_id] || {};
                  return (
                    <TableRow key={row.user_id}>
                      <TableCell>{row.user_name || "—"}</TableCell>
                      <TableCell>{row.user_email || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{row.reason || "Missing revops mapping"}</TableCell>
                      <TableCell>{formatNumber(row.recordings)}</TableCell>
                      <TableCell>
                        <CreatableSelect
                          value={draft.specialty ?? row.specialty ?? ""}
                          options={specialties}
                          placeholder="Specialty"
                          className={INPUT_CLASS}
                          onChange={(value) => updateDraft(row.user_id, "specialty", value)}
                          onCreate={(value) => remember("specialties", value)}
                        />
                      </TableCell>
                      <TableCell>
                        <CreatableSelect
                          value={draft.org ?? row.super_org ?? ""}
                          options={superOrgs}
                          placeholder="Super org"
                          className={INPUT_CLASS}
                          onChange={(value) => updateDraft(row.user_id, "org", value)}
                          onCreate={(value) => remember("super_orgs", value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          disabled={!row.has_doctor || savingId === row.user_id}
                          onClick={() => handleLog(row)}
                        >
                          {savingId === row.user_id ? "Logging…" : "Log"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Pricing cleaning</CardTitle>
        <CardDescription>
          Super orgs with licensed users this month but no cost per user. Log is used on the next
          report load — no deploy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Super org</TableHead>
                <TableHead>Licensed</TableHead>
                <TableHead>Monthly recordings</TableHead>
                <TableHead>Cost per user</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pricingRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-muted-foreground">
                    No pricing cleaning needed
                  </TableCell>
                </TableRow>
              ) : (
                pricingRows.map((row) => (
                  <TableRow key={row.super_org}>
                    <TableCell>{row.super_org}</TableCell>
                    <TableCell>{formatNumber(row.licensed)}</TableCell>
                    <TableCell>{formatNumber(row.recordings)}</TableCell>
                    <TableCell>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={INPUT_CLASS}
                        placeholder="e.g. 300"
                        value={priceDrafts[row.super_org] ?? ""}
                        onChange={(e) =>
                          setPriceDrafts((prev) => ({ ...prev, [row.super_org]: e.target.value }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        disabled={savingId === row.super_org}
                        onClick={() => handleLogPrice(row)}
                      >
                        {savingId === row.super_org ? "Logging…" : "Log"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}
